import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Drzava, Grupa, Nalog, NivoPakovanja, UnosPakovanja } from '../lib/tipovi'

const NIVOI: { vrijednost: NivoPakovanja; oznaka: string }[] = [
  { vrijednost: 'komad', oznaka: 'Komad' },
  { vrijednost: 'kesa', oznaka: 'Kesa' },
  { vrijednost: 'karton', oznaka: 'Karton' },
  { vrijednost: 'paleta', oznaka: 'Paleta' },
]

const JEDINICE = ['kom', 'kg', 'l', 'm', 'pak']

type Faza = 'unos' | 'slike'

type Fotografija = {
  id: string
  putanja_original: string
  putanja_obradjena: string | null
  glavna: boolean
  izvor: 'sopstvena' | 'dobavljac'
  status: 'na_cekanju' | 'obradjena' | 'odbacena'
}

const oznakaStatusa: Record<Fotografija['status'], string> = {
  na_cekanju: 'Na čekanju',
  obradjena: 'Obrađena',
  odbacena: 'Odbačena',
}

/** SHA-256 fajla, isti otisak koji baza čuva u `hash`. Sprečava da ista slika dvaput uđe
 *  na isti artikal — što je uvijek greška operatera, nikad namjera. */
async function otisak(fajl: File): Promise<string> {
  const sazetak = await crypto.subtle.digest('SHA-256', await fajl.arrayBuffer())
  return Array.from(new Uint8Array(sazetak))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

function nastavakZa(tip: string): string {
  if (tip === 'image/png') return 'png'
  if (tip === 'image/webp') return 'webp'
  return 'jpg'
}

/** Ručni unos artikla (ZAHTJEV §2) sa fotografijama (C20).
 *
 *  Ekran ne odlučuje ništa: `artikli` prima upis samo kroz politiku `upis_admin`, pa je
 *  sakriveno dugme udobnost a ne zaštita. Isto važi za slike — glavnu postavlja RPC, a
 *  obradu pušta modul u bazi.
 *
 *  Slike dolaze poslije snimanja artikla, ne prije: putanja u bucket-u je
 *  `{firma}/{artikal}/...`, pa artikal mora postojati da bi slika imala gdje da stane.
 */
export function NoviArtikal({
  nalog,
  naOdustani,
  naSacuvan,
}: {
  nalog: Nalog
  naOdustani: () => void
  naSacuvan: () => void
}) {
  const [faza, postaviFazu] = useState<Faza>('unos')
  const [artikalId, postaviArtikalId] = useState<string | null>(null)

  const [sifra, postaviSifru] = useState('')
  const [naziv, postaviNaziv] = useState('')
  const [grupaId, postaviGrupu] = useState('')
  const [jedinica, postaviJedinicu] = useState('kom')
  const [brend, postaviBrend] = useState('')
  const [kataloskiBroj, postaviKataloskiBroj] = useState('')
  const [zemlja, postaviZemlju] = useState('')
  const [kontaktSaHranom, postaviKontakt] = useState(false)
  const [opasnaRoba, postaviOpasnu] = useState(false)
  const [pratiSeriju, postaviPratiSeriju] = useState(false)
  const [usloviCuvanja, postaviUslove] = useState('')
  const [rezervaDana, postaviRezervu] = useState('7')
  const [horizontDana, postaviHorizont] = useState('45')

  const [pakovanja, postaviPakovanja] = useState<UnosPakovanja[]>([
    { nivo: 'kesa', sadrzi_osn: '', bar_kod: '', moze_se_prodati: true, podrazumijevano_za_prodaju: true },
  ])

  const [grupe, postaviGrupe] = useState<Grupa[]>([])
  const [drzave, postaviDrzave] = useState<Drzava[]>([])
  const [fotografije, postaviFotografije] = useState<Fotografija[]>([])
  const [pregledi, postaviPreglede] = useState<Record<string, string>>({})
  const [izvorNovih, postaviIzvorNovih] = useState<'sopstvena' | 'dobavljac'>('sopstvena')

  const [radi, postaviRadi] = useState(false)
  const [greska, postaviGresku] = useState<string | null>(null)
  const [poruka, postaviPoruku] = useState<string | null>(null)

  const jeAdmin = nalog.uloga === 'admin'

  useEffect(() => {
    void (async () => {
      const [g, d] = await Promise.all([
        supabase.from('grupe').select('id, naziv').order('naziv'),
        supabase.from('drzave').select('kod, naziv').order('naziv'),
      ])
      postaviGrupe((g.data ?? []) as Grupa[])
      postaviDrzave((d.data ?? []) as Drzava[])
    })()
  }, [])

  function dodajPakovanje() {
    postaviPakovanja((p) => [
      ...p,
      { nivo: 'karton', sadrzi_osn: '', bar_kod: '', moze_se_prodati: true, podrazumijevano_za_prodaju: false },
    ])
  }

  function izmijeniPakovanje(indeks: number, izmjena: Partial<UnosPakovanja>) {
    postaviPakovanja((p) => p.map((red, i) => (i === indeks ? { ...red, ...izmjena } : red)))
  }

  async function sacuvaj(dogadjaj: React.FormEvent) {
    dogadjaj.preventDefault()
    postaviGresku(null)
    postaviRadi(true)

    const { data, error } = await supabase
      .from('artikli')
      .insert({
        firma_id: nalog.firmaId,
        sifra: sifra.trim(),
        naziv: naziv.trim(),
        grupa_id: grupaId || null,
        osnovna_jedinica: jedinica.trim(),
        brend: brend.trim() || null,
        kataloski_broj: kataloskiBroj.trim() || null,
        zemlja_porijekla: zemlja || null,
        kontakt_sa_hranom: kontaktSaHranom,
        opasna_roba: opasnaRoba,
        prati_seriju_i_rok: pratiSeriju,
        uslovi_cuvanja: usloviCuvanja.trim() || null,
        sigurnosna_rezerva_dana: Number(rezervaDana),
        horizont_dana: Number(horizontDana),
      })
      .select('id')
      .single()

    if (error || !data) {
      postaviRadi(false)
      // 23505 je jedina greška koju operater može sam da ispravi, pa dobija svoje riječi.
      postaviGresku(
        error?.code === '23505'
          ? `Šifra „${sifra.trim()}" već postoji u ovoj firmi.`
          : (error?.message ?? 'Artikal nije sačuvan.'),
      )
      return
    }

    const redovi = pakovanja
      .filter((p) => p.sadrzi_osn.trim() !== '')
      .map((p) => ({
        firma_id: nalog.firmaId,
        artikal_id: data.id,
        nivo: p.nivo,
        sadrzi_osn: Number(p.sadrzi_osn),
        bar_kod: p.bar_kod.trim() || null,
        moze_se_prodati: p.moze_se_prodati,
        podrazumijevano_za_prodaju: p.podrazumijevano_za_prodaju,
      }))

    if (redovi.length > 0) {
      const { error: greskaPakovanja } = await supabase.from('pakovanja').insert(redovi)
      if (greskaPakovanja) {
        // Artikal je već upisan; pakovanje se dodaje kasnije, pa se ne pravi lažni utisak
        // da ništa nije sačuvano.
        postaviGresku(`Artikal je sačuvan, ali pakovanje nije: ${greskaPakovanja.message}`)
      }
    }

    postaviArtikalId(data.id)
    postaviFazu('slike')
    postaviRadi(false)
  }

  async function ucitajFotografije(zaArtikal: string) {
    const { data, error } = await supabase
      .from('artikal_fotografije')
      .select('id, putanja_original, putanja_obradjena, glavna, izvor, status')
      .eq('artikal_id', zaArtikal)
      .order('kreirano')

    if (error) {
      postaviGresku(error.message)
      return
    }

    const redovi = (data ?? []) as Fotografija[]
    postaviFotografije(redovi)

    // Bucket je privatan, pa se pregled gleda kroz potpisani link koji ističe za 5 minuta.
    const linkovi: Record<string, string> = {}
    await Promise.all(
      redovi.map(async (f) => {
        const putanja = f.putanja_obradjena ?? f.putanja_original
        const { data: link } = await supabase.storage
          .from('fotografije')
          .createSignedUrl(putanja, 300)
        if (link?.signedUrl) linkovi[f.id] = link.signedUrl
      }),
    )
    postaviPreglede(linkovi)
  }

  async function dodajSlike(fajlovi: FileList | null) {
    if (!fajlovi || !artikalId) return
    postaviGresku(null)
    postaviPoruku(null)
    postaviRadi(true)

    for (const fajl of Array.from(fajlovi)) {
      const hash = await otisak(fajl)
      const putanja = `${nalog.firmaId}/${artikalId}/${hash}.${nastavakZa(fajl.type)}`

      const { error: greskaUpload } = await supabase.storage
        .from('fotografije')
        .upload(putanja, fajl, { contentType: fajl.type })

      // Fajl koji već stoji na toj putanji je ista slika (ime je njen otisak), pa se
      // upload preskače a red se svejedno pokušava — bazi je otisak jedinstven.
      if (greskaUpload && !greskaUpload.message.toLowerCase().includes('exists')) {
        postaviGresku(`${fajl.name}: ${greskaUpload.message}`)
        continue
      }

      const { error: greskaReda } = await supabase.from('artikal_fotografije').insert({
        firma_id: nalog.firmaId,
        artikal_id: artikalId,
        putanja_original: putanja,
        izvor: izvorNovih,
        hash,
        kreirao: nalog.korisnikId,
      })

      if (greskaReda) {
        postaviGresku(
          greskaReda.code === '23505'
            ? `${fajl.name}: ista slika je već dodata ovom artiklu.`
            : `${fajl.name}: ${greskaReda.message}`,
        )
      }
    }

    await ucitajFotografije(artikalId)
    postaviRadi(false)
  }

  async function pozovi(rpc: string, id: string) {
    postaviGresku(null)
    postaviPoruku(null)
    postaviRadi(true)
    const { error } = await supabase.rpc(rpc, { p_id: id })
    if (error) postaviGresku(error.message)
    if (artikalId) await ucitajFotografije(artikalId)
    postaviRadi(false)
  }

  async function obradi(id: string) {
    postaviGresku(null)
    postaviPoruku(null)
    postaviRadi(true)

    const { error } = await supabase.functions.invoke('foto-obrada', { body: { foto_id: id } })
    if (error) {
      postaviGresku(
        'Obrada nije uspjela. Slika ostaje na čekanju i može se poslati ponovo.',
      )
    } else {
      postaviPoruku('Slika je obrađena. Original je sačuvan.')
    }

    if (artikalId) await ucitajFotografije(artikalId)
    postaviRadi(false)
  }

  if (!jeAdmin) {
    return (
      <div className="prazno" data-testid="artikal-bez-prava">
        Artikle u katalog dodaje admin firme (C17).
      </div>
    )
  }

  if (faza === 'slike' && artikalId) {
    return (
      <div className="obrazac" data-testid="artikal-slike">
        <h2 className="odjeljak__naslov">Fotografije artikla</h2>
        <p className="razlog">
          Obrada se ne dešava sama: original ostaje zauvijek, a čišćenje pozadine se šalje
          tek kad klikneš „Obradi" (ZAHTJEV §2.1).
        </p>

        {greska && (
          <div className="greska" role="alert" data-testid="artikal-greska">
            {greska}
          </div>
        )}
        {poruka && (
          <div className="ishod" data-testid="artikal-poruka">
            {poruka}
          </div>
        )}

        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="izvor-slike">
            Izvor novih slika
          </label>
          <select
            id="izvor-slike"
            className="polje"
            value={izvorNovih}
            onChange={(e) => postaviIzvorNovih(e.target.value as 'sopstvena' | 'dobavljac')}
          >
            <option value="sopstvena">Sopstvena (uslikana u magacinu)</option>
            <option value="dobavljac">Preuzeta sa sajta dobavljača</option>
          </select>
        </div>

        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="slike">
            Dodaj slike
          </label>
          <input
            id="slike"
            className="polje"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            disabled={radi}
            onChange={(e) => void dodajSlike(e.target.files)}
            data-testid="artikal-slike-unos"
          />
        </div>

        {fotografije.length === 0 ? (
          <div className="prazno">Artikal još nema nijednu sliku.</div>
        ) : (
          <ul className="slike" data-testid="artikal-slike-lista">
            {fotografije.map((f) => (
              <li key={f.id} className="slike__stavka">
                {pregledi[f.id] ? (
                  <img className="slike__pregled" src={pregledi[f.id]} alt="" />
                ) : (
                  <div className="slike__pregled slike__pregled--prazno" aria-hidden="true" />
                )}
                <div className="slike__opis">
                  <div>
                    {f.glavna && <strong>Glavna · </strong>}
                    {oznakaStatusa[f.status]}
                    <span className="tabela__tiho">
                      {' '}
                      · {f.izvor === 'sopstvena' ? 'sopstvena' : 'dobavljač'}
                    </span>
                  </div>
                  <div className="slike__dugmad">
                    <button
                      type="button"
                      className="dugme dugme--sporedno"
                      disabled={radi || f.glavna || f.status === 'odbacena'}
                      onClick={() => void pozovi('postavi_glavnu_fotografiju', f.id)}
                    >
                      Glavna
                    </button>
                    {f.status === 'na_cekanju' && (
                      <button
                        type="button"
                        className="dugme dugme--sporedno"
                        disabled={radi}
                        onClick={() => void obradi(f.id)}
                        data-testid="artikal-obradi"
                      >
                        Obradi
                      </button>
                    )}
                    {f.status !== 'odbacena' && (
                      <button
                        type="button"
                        className="dugme dugme--sporedno"
                        disabled={radi}
                        onClick={() => void pozovi('foto_odbaci', f.id)}
                      >
                        Odbaci
                      </button>
                    )}
                    {f.status !== 'na_cekanju' && (
                      <button
                        type="button"
                        className="dugme dugme--sporedno"
                        disabled={radi}
                        onClick={() => void pozovi('foto_ponovo', f.id)}
                      >
                        Vrati na čekanje
                      </button>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="obrazac__dugme">
          <button
            type="button"
            className="dugme dugme--glavno"
            onClick={naSacuvan}
            data-testid="artikal-gotovo"
          >
            Gotovo
          </button>
        </div>
      </div>
    )
  }

  return (
    <form className="obrazac" onSubmit={sacuvaj} data-testid="artikal-obrazac">
      <h2 className="odjeljak__naslov">Novi artikal</h2>

      {greska && (
        <div className="greska" role="alert" data-testid="artikal-greska">
          {greska}
        </div>
      )}

      <div className="obrazac__red">
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="sifra">
            Šifra
          </label>
          <input
            id="sifra"
            className="polje"
            required
            value={sifra}
            onChange={(e) => postaviSifru(e.target.value)}
            data-testid="artikal-sifra"
          />
        </div>
        <div className="obrazac__polje obrazac__polje--siroko">
          <label className="polje-oznaka" htmlFor="naziv">
            Naziv
          </label>
          <input
            id="naziv"
            className="polje"
            required
            value={naziv}
            onChange={(e) => postaviNaziv(e.target.value)}
            data-testid="artikal-naziv"
          />
        </div>
      </div>

      <div className="obrazac__red">
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="grupa">
            Grupa
          </label>
          <select
            id="grupa"
            className="polje"
            value={grupaId}
            onChange={(e) => postaviGrupu(e.target.value)}
          >
            <option value="">—</option>
            {grupe.map((g) => (
              <option key={g.id} value={g.id}>
                {g.naziv}
              </option>
            ))}
          </select>
        </div>
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="jedinica">
            Osnovna jedinica
          </label>
          <input
            id="jedinica"
            className="polje"
            required
            list="jedinice"
            value={jedinica}
            onChange={(e) => postaviJedinicu(e.target.value)}
            data-testid="artikal-jedinica"
          />
          <datalist id="jedinice">
            {JEDINICE.map((j) => (
              <option key={j} value={j} />
            ))}
          </datalist>
        </div>
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="brend">
            Brend
          </label>
          <input
            id="brend"
            className="polje"
            value={brend}
            onChange={(e) => postaviBrend(e.target.value)}
          />
        </div>
      </div>

      <div className="obrazac__red">
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="kataloski">
            Kataloški broj
          </label>
          <input
            id="kataloski"
            className="polje"
            value={kataloskiBroj}
            onChange={(e) => postaviKataloskiBroj(e.target.value)}
          />
        </div>
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="zemlja">
            Zemlja porijekla
          </label>
          <select
            id="zemlja"
            className="polje"
            value={zemlja}
            onChange={(e) => postaviZemlju(e.target.value)}
          >
            <option value="">—</option>
            {drzave.map((d) => (
              <option key={d.kod} value={d.kod}>
                {d.naziv}
              </option>
            ))}
          </select>
        </div>
      </div>

      <h3 className="odjeljak__naslov">Rukovanje</h3>
      <div className="obrazac__red">
        <label className="obrazac__prekidac">
          <input
            type="checkbox"
            checked={kontaktSaHranom}
            onChange={(e) => postaviKontakt(e.target.checked)}
            data-testid="artikal-hrana"
          />
          Dolazi u kontakt sa hranom
        </label>
        <label className="obrazac__prekidac">
          <input
            type="checkbox"
            checked={opasnaRoba}
            onChange={(e) => postaviOpasnu(e.target.checked)}
          />
          Opasna roba
        </label>
        <label className="obrazac__prekidac">
          <input
            type="checkbox"
            checked={pratiSeriju}
            onChange={(e) => postaviPratiSeriju(e.target.checked)}
            data-testid="artikal-serija"
          />
          Prati seriju i rok
        </label>
      </div>

      <div className="obrazac__polje">
        <label className="polje-oznaka" htmlFor="uslovi">
          Uslovi čuvanja
        </label>
        <input
          id="uslovi"
          className="polje"
          placeholder="npr. suvo, do 25 °C"
          value={usloviCuvanja}
          onChange={(e) => postaviUslove(e.target.value)}
        />
      </div>

      <h3 className="odjeljak__naslov">Zaliha</h3>
      <p className="razlog">
        Sigurnosna rezerva i horizont ulaze u predlog za naručivanje (C31); mijenjaju se i
        kasnije, po artiklu.
      </p>
      <div className="obrazac__red">
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="rezerva">
            Sigurnosna rezerva (dana)
          </label>
          <input
            id="rezerva"
            className="polje desno"
            type="number"
            min="0"
            value={rezervaDana}
            onChange={(e) => postaviRezervu(e.target.value)}
          />
        </div>
        <div className="obrazac__polje">
          <label className="polje-oznaka" htmlFor="horizont">
            Horizont (dana)
          </label>
          <input
            id="horizont"
            className="polje desno"
            type="number"
            min="1"
            value={horizontDana}
            onChange={(e) => postaviHorizont(e.target.value)}
          />
        </div>
      </div>

      <h3 className="odjeljak__naslov">Pakovanja</h3>
      <p className="razlog">
        Roba se naručuje i prima u pakovanju, ne u komadu (ZAHTJEV §2). Prazan red se
        preskače.
      </p>
      {pakovanja.map((p, i) => (
        <div className="obrazac__red" key={i}>
          <div className="obrazac__polje">
            <label className="polje-oznaka" htmlFor={`nivo-${i}`}>
              Nivo
            </label>
            <select
              id={`nivo-${i}`}
              className="polje"
              value={p.nivo}
              onChange={(e) => izmijeniPakovanje(i, { nivo: e.target.value as NivoPakovanja })}
            >
              {NIVOI.map((n) => (
                <option key={n.vrijednost} value={n.vrijednost}>
                  {n.oznaka}
                </option>
              ))}
            </select>
          </div>
          <div className="obrazac__polje">
            <label className="polje-oznaka" htmlFor={`sadrzi-${i}`}>
              Sadrži ({jedinica})
            </label>
            <input
              id={`sadrzi-${i}`}
              className="polje desno"
              type="number"
              min="0"
              step="0.001"
              value={p.sadrzi_osn}
              onChange={(e) => izmijeniPakovanje(i, { sadrzi_osn: e.target.value })}
              data-testid={`artikal-sadrzi-${i}`}
            />
          </div>
          <div className="obrazac__polje">
            <label className="polje-oznaka" htmlFor={`barkod-${i}`}>
              Bar kod
            </label>
            <input
              id={`barkod-${i}`}
              className="polje"
              inputMode="numeric"
              value={p.bar_kod}
              onChange={(e) => izmijeniPakovanje(i, { bar_kod: e.target.value })}
              data-testid={`artikal-barkod-${i}`}
            />
          </div>
          <label className="obrazac__prekidac">
            <input
              type="checkbox"
              checked={p.podrazumijevano_za_prodaju}
              onChange={(e) =>
                izmijeniPakovanje(i, { podrazumijevano_za_prodaju: e.target.checked })
              }
            />
            Podrazumijevano za prodaju
          </label>
        </div>
      ))}
      <button type="button" className="veza-tekst" onClick={dodajPakovanje}>
        + Još jedno pakovanje
      </button>

      <div className="obrazac__dugme">
        <button
          type="submit"
          className="dugme dugme--glavno"
          disabled={radi}
          data-testid="artikal-sacuvaj"
        >
          {radi ? 'Čuvam…' : 'Sačuvaj i dodaj slike'}
        </button>
        <button type="button" className="dugme dugme--sporedno" onClick={naOdustani}>
          Odustani
        </button>
      </div>
    </form>
  )
}
